import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import CustomSelect from '../components/CustomSelect';
import RowActions from '../components/RowActions';

interface SupportPlan {
  id: number;
  residentId: number;
  residentName: string;
  title: string;
  description: string;
  category: string;
  goals: Array<{
    description: string;
    targetDate: string | null;
    isCompleted: boolean;
    completedDate: string | null;
  }>;
  reviewDate: string;
  status: string;
  priority: string;
  createdAt: string;
  updatedAt: string;
  createdByName: string;
  createdByRole: string;
}

interface Resident {
  id: number;
  name: string;
}

const SupportPlans: React.FC = () => {
  const [plans, setPlans] = useState<SupportPlan[]>([]);
  const [residents, setResidents] = useState<Resident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SupportPlan | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [residentFilter, setResidentFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (residentFilter !== 'all') params.append('residentId', residentFilter);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());

      const response = await api.get(`/support-plans?${params}`);
      if (response.data.success) {
        setPlans(response.data.data);
      }
    } catch (err: any) {
      console.error('Error fetching support plans:', err);
      setError(err.response?.data?.message || 'Failed to fetch support plans');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, residentFilter, searchQuery]);

  const fetchResidents = async () => {
    try {
      const response = await api.get('/residents/assigned');
      if (response.data.success) {
        setResidents(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching residents:', err);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchResidents();
  }, [fetchPlans]);

  const handleAddPlan = () => {
    setEditingPlan(null);
    setShowModal(true);
  };

  const handleEditPlan = (plan: SupportPlan) => {
    setEditingPlan(plan);
    setShowModal(true);
  };

  const handleDeletePlan = async (planId: number) => {
    if (!window.confirm('Are you sure you want to delete this support plan?')) {
      return;
    }

    try {
      const response = await api.delete(`/support-plans/${planId}`);
      if (response.data.success) {
        setMessage('Support plan deleted successfully');
        fetchPlans();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete support plan');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      healthcare: 'bg-blue-100 text-blue-800',
      mobility: 'bg-green-100 text-green-800',
      nutrition: 'bg-yellow-100 text-yellow-800',
      social: 'bg-primary-100 text-primary-800',
      mental_health: 'bg-pink-100 text-pink-800',
      safety: 'bg-red-100 text-red-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.other;
  };

  const getPriorityColor = (priority: string) => {
    const colors: { [key: string]: string } = {
      low: 'bg-green-100 text-green-800',
      normal: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || colors.normal;
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.active;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Support Plans
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage care support plans for residents
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={handleAddPlan}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Support Plan
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {message && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {message}
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white card-list p-4 mb-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title or description..."
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Resident</label>
            <CustomSelect
              value={residentFilter}
              onChange={setResidentFilter}
              options={[
                { value: 'all', label: 'All Residents' },
                ...residents.map(resident => ({ value: String(resident.id), label: resident.name })),
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <CustomSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'active', label: 'Active' },
                { value: 'pending', label: 'Pending' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <CustomSelect
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={[
                { value: 'all', label: 'All Categories' },
                { value: 'healthcare', label: 'Healthcare' },
                { value: 'mobility', label: 'Mobility' },
                { value: 'nutrition', label: 'Nutrition' },
                { value: 'social', label: 'Social' },
                { value: 'mental_health', label: 'Mental Health' },
                { value: 'safety', label: 'Safety' },
                { value: 'other', label: 'Other' },
              ]}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setStatusFilter('all');
                setCategoryFilter('all');
                setResidentFilter('all');
                setSearchQuery('');
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Support Plans List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : plans.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No support plans</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new support plan.</p>
          <div className="mt-6">
            <button
              onClick={handleAddPlan}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Support Plan
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white card-list overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {plans.map((plan) => (
              <li key={plan.id}>
                <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-lg font-medium text-blue-600 truncate">{plan.title}</p>
                        <div className="ml-2 flex items-center space-x-2">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(plan.status)}`}>
                            {plan.status}
                          </span>
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(plan.priority)}`}>
                            {plan.priority}
                          </span>
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoryColor(plan.category)}`}>
                            {plan.category}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between">
                        <div className="sm:flex">
                          <p className="flex items-center text-sm text-gray-500">
                            <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {plan.residentName}
                          </p>
                          <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                            <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Review: {formatDate(plan.reviewDate)}
                          </p>
                        </div>
                      </div>
                      <p className="mt-2 text-sm text-gray-600">{plan.description}</p>
                      {plan.goals && plan.goals.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-700">Goals:</p>
                          <ul className="mt-1 space-y-1">
                            {plan.goals.slice(0, 3).map((goal, index) => (
                              <li key={index} className="text-sm text-gray-600 flex items-center">
                                <svg className={`mr-2 h-4 w-4 ${goal.isCompleted ? 'text-green-500' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                {goal.description}
                              </li>
                            ))}
                            {plan.goals.length > 3 && (
                              <li className="text-sm text-gray-500 ml-6">+{plan.goals.length - 3} more goals</li>
                            )}
                          </ul>
                        </div>
                      )}
                      <div className="mt-2 flex items-center text-xs text-gray-500">
                        <span>Created by {plan.createdByName} on {formatDate(plan.createdAt)}</span>
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0 flex items-center">
                      <RowActions
                        actions={[
                          { label: 'Edit', tone: 'default', onClick: () => handleEditPlan(plan) },
                          { label: 'Delete', tone: 'danger', onClick: () => handleDeletePlan(plan.id) },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <SupportPlanModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingPlan(null);
          }}
          plan={editingPlan}
          residents={residents}
          onSuccess={() => {
            setShowModal(false);
            setEditingPlan(null);
            fetchPlans();
            setMessage(editingPlan ? 'Support plan updated successfully' : 'Support plan created successfully');
            setTimeout(() => setMessage(''), 3000);
          }}
        />
      )}
    </div>
  );
};

// Support Plan Modal Component
interface SupportPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: SupportPlan | null;
  residents: Resident[];
  onSuccess: () => void;
}

const SupportPlanModal: React.FC<SupportPlanModalProps> = ({ isOpen, onClose, plan, residents, onSuccess }) => {
  const [formData, setFormData] = useState({
    residentId: plan?.residentId.toString() || '',
    title: plan?.title || '',
    description: plan?.description || '',
    category: plan?.category || 'other',
    reviewDate: plan?.reviewDate ? plan.reviewDate.split('T')[0] : '',
    priority: plan?.priority || 'normal',
    status: plan?.status || 'active',
    goals: plan?.goals || []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const url = plan ? `/support-plans/${plan.id}` : '/support-plans';
      const method = plan ? 'put' : 'post';
      
      const response = await api[method](url, formData);
      
      if (response.data.success) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save support plan');
    } finally {
      setLoading(false);
    }
  };

  const addGoal = () => {
    setFormData({
      ...formData,
      goals: [...formData.goals, { description: '', targetDate: null, isCompleted: false, completedDate: null }]
    });
  };

  const removeGoal = (index: number) => {
    setFormData({
      ...formData,
      goals: formData.goals.filter((_, i) => i !== index)
    });
  };

  const updateGoal = (index: number, field: string, value: any) => {
    const updatedGoals = [...formData.goals];
    updatedGoals[index] = { ...updatedGoals[index], [field]: value };
    setFormData({ ...formData, goals: updatedGoals });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white mb-10">
        <div className="flex justify-between items-center pb-4 border-b">
          <h3 className="text-xl font-bold text-gray-900">
            {plan ? 'Edit Support Plan' : 'New Support Plan'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">
            ×
          </button>
        </div>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Resident *</label>
              <div className="mt-1">
                <CustomSelect
                  value={formData.residentId}
                  onChange={(v) => setFormData({ ...formData, residentId: v })}
                  placeholder="Select Resident"
                  options={[
                    { value: '', label: 'Select Resident' },
                    ...residents.map(resident => ({ value: String(resident.id), label: resident.name })),
                  ]}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Review Date *</label>
              <input
                type="date"
                value={formData.reviewDate}
                onChange={(e) => setFormData({ ...formData, reviewDate: e.target.value })}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <div className="mt-1">
                <CustomSelect
                  value={formData.category}
                  onChange={(v) => setFormData({ ...formData, category: v })}
                  options={[
                    { value: 'healthcare', label: 'Healthcare' },
                    { value: 'mobility', label: 'Mobility' },
                    { value: 'nutrition', label: 'Nutrition' },
                    { value: 'social', label: 'Social' },
                    { value: 'mental_health', label: 'Mental Health' },
                    { value: 'safety', label: 'Safety' },
                    { value: 'other', label: 'Other' },
                  ]}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Priority</label>
              <div className="mt-1">
                <CustomSelect
                  value={formData.priority}
                  onChange={(v) => setFormData({ ...formData, priority: v })}
                  options={[
                    { value: 'low', label: 'Low' },
                    { value: 'normal', label: 'Normal' },
                    { value: 'high', label: 'High' },
                    { value: 'urgent', label: 'Urgent' },
                  ]}
                />
              </div>
            </div>

            {plan && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <div className="mt-1">
                  <CustomSelect
                    value={formData.status}
                    onChange={(v) => setFormData({ ...formData, status: v })}
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'cancelled', label: 'Cancelled' },
                    ]}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows={3}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
            />
          </div>

          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Goals</label>
              <button
                type="button"
                onClick={addGoal}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                + Add Goal
              </button>
            </div>
            {formData.goals.map((goal, index) => (
              <div key={index} className="mb-3 p-3 border border-gray-200 rounded-md">
                <div className="flex justify-between items-start mb-2">
                  <input
                    type="text"
                    value={goal.description}
                    onChange={(e) => updateGoal(index, 'description', e.target.value)}
                    placeholder="Goal description"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => removeGoal(index)}
                    className="ml-2 text-red-600 hover:text-red-800"
                  >
                    ×
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={goal.targetDate || ''}
                    onChange={(e) => updateGoal(index, 'targetDate', e.target.value)}
                    placeholder="Target date"
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
                  />
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={goal.isCompleted}
                      onChange={(e) => updateGoal(index, 'isCompleted', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm">Completed</span>
                  </label>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : plan ? 'Update Plan' : 'Create Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportPlans;
